import { useState, useCallback, useMemo } from 'react';
import { captureAppError } from '@leagueplatform/observability';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import {
  usePersonalInformation,
  useUpdateUserProfile,
} from 'hooks/use-personal-information.hook';
import { CoverageExperience, SENTRY_CONSTANTS } from 'common/constants';
import { useGetAppInitDetails } from './use-get-app-init-details';

interface UseOnboardingFlowReturn {
  // Data
  userData: any;
  isLoading: boolean;

  // State
  isSetupComplete: boolean;
  isOnboardingComplete: boolean;

  // Actions
  handleAccountSetupComplete: () => void;
  handleOnboardingComplete: () => void;
  setHasCompletedGetStarted: (completed: boolean) => void;
  markAccountSetupCompletedWhenSkipping: () => Promise<void>;

  // Computed values
  coverageExperience: CoverageExperience;
  accountSetupCompleted: boolean;
  getStartedCompleted: boolean;
  showOnboardingField: boolean;
}

export const useOnboardingFlow = (): UseOnboardingFlowReturn => {
  const navigate = useNavigate();
  const { data: userData, isLoading } = usePersonalInformation();
  const { data: appInitDetails } = useGetAppInitDetails();
  const { updateUserProfile } = useUpdateUserProfile({});

  const [hasCompletedSetup, setHasCompletedSetup] = useState(false);
  const [hasCompletedGetStarted, setHasCompletedGetStarted] = useState(false);

  const customFields = userData?.customFields || [];

  const coverageExperience =
    (customFields.find((field) => field.name === 'coverage_experience')
      ?.value as CoverageExperience) || CoverageExperience.Active;

  const accountSetupCompleted = customFields.some(
    (field) =>
      field.name === 'account_setup_completed' && field.value === 'true',
  );
  const getStartedCompleted = customFields.some(
    (field) => field.name === 'get_started_completed' && field.value === 'true',
  );

  const showOnboardingField = appInitDetails?.segmentation?.showOnboardingFlow;

  const { isSetupComplete, isOnboardingComplete } = useMemo(() => {
    if (isLoading || !userData) {
      return { isSetupComplete: false, isOnboardingComplete: false };
    }

    const shouldBeSetupComplete = accountSetupCompleted || hasCompletedSetup;

    const shouldSkipGetStarted = !showOnboardingField || getStartedCompleted;

    if (shouldSkipGetStarted && shouldBeSetupComplete) {
      return { isSetupComplete: true, isOnboardingComplete: true };
    }

    if (shouldSkipGetStarted && !shouldBeSetupComplete) {
      return { isSetupComplete: false, isOnboardingComplete: false };
    }

    const shouldCompleteOnboarding =
      shouldBeSetupComplete && (getStartedCompleted || hasCompletedGetStarted);

    return {
      isSetupComplete: shouldBeSetupComplete,
      isOnboardingComplete: shouldCompleteOnboarding,
    };
  }, [
    isLoading,
    userData,
    accountSetupCompleted,
    getStartedCompleted,
    hasCompletedSetup,
    hasCompletedGetStarted,
    showOnboardingField,
  ]);

  const handleAccountSetupComplete = useCallback(() => {
    setHasCompletedSetup(true);

    if (!showOnboardingField) {
      // const targetUrl = userData?.onboardingDeeplink || '/';
      // const url = new URL(targetUrl, window.location.origin);
      // url.searchParams.set('fs', 'true');
      // navigate(url.pathname + url.search);
      navigate('/home');
    }
  }, [navigate, showOnboardingField]);

  const handleOnboardingComplete = useCallback(() => {
    setHasCompletedGetStarted(true);
    const targetUrl = userData?.onboardingDeeplink || '/';
    const url = new URL(targetUrl, window.location.origin);
    url.searchParams.set('fs', 'true');
    navigate(url.pathname + url.search);
  }, [userData?.onboardingDeeplink, navigate]);

  const markAccountSetupCompletedWhenSkipping = useCallback(async () => {
    if (accountSetupCompleted) return;

    try {
      await updateUserProfile({
        custom_fields: [
          ...(userData?.customFields || []).filter(
            (field) => field.name !== 'account_setup_completed',
          ),
          { name: 'account_setup_completed', value: 'true' },
        ],
      });
      setHasCompletedSetup(true);
    } catch (error) {
      captureAppError(error as Error, {
        ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      });
    }
  }, [accountSetupCompleted, updateUserProfile, userData?.customFields]);

  return {
    // Data
    userData,
    isLoading,

    // State
    isSetupComplete,
    isOnboardingComplete,

    // Actions
    handleAccountSetupComplete,
    handleOnboardingComplete,
    setHasCompletedGetStarted,
    markAccountSetupCompletedWhenSkipping,

    // Computed values
    coverageExperience,
    accountSetupCompleted,
    getStartedCompleted,
    showOnboardingField: showOnboardingField ?? false,
  };
};
